import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { LicenseService } from '../../../../services/remote/license.service';
import { LicenseStatus } from '../../../../types/license';
import {
  BrandingSettings,
  LicenseDetailsBrandingComponent,
} from './license-details-branding/license-details-branding.component';
import { LicenseDetailsAssistantsComponent } from './license-details-assistants/license-details-assistants.component';
import { LicenseDetailsGeneralComponent } from './license-details-general/license-details-general.component';
import { LicenseDetailsModelComponent } from './license-details-model/license-details-model.component';
import { LicenseDetailsStorageComponent } from './license-details-storage/license-details-storage.component';
import { LicenseDetailsRemoteSourcesComponent } from './license-details-remote-sources/license-details-remote-sources.component';

@Component({
  selector: 'cognos-license-details-dialog',
  imports: [
    ButtonModule,
    TableModule,
    TagModule,
    FormsModule,
    ReactiveFormsModule,
    FloatLabelModule,
    InputTextModule,
    TextareaModule,
    DividerModule,
    InputNumberModule,
    SelectModule,
    TabsModule,
    Dialog,
    LicenseDetailsGeneralComponent,
    LicenseDetailsModelComponent,
    LicenseDetailsAssistantsComponent,
    LicenseDetailsBrandingComponent,
    LicenseDetailsStorageComponent,
    LicenseDetailsRemoteSourcesComponent,
  ],
  templateUrl: './license-details-dialog.component.html',
  styleUrl: './license-details-dialog.component.scss',
})
export class LicenseDetailsDialogComponent {
  @Input() isOpen: boolean = false;
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() licenseUpdated = new EventEmitter<{
    id: string;
    status: LicenseStatus;
  }>();

  @Input({ required: true }) companyName: string = '';
  @Input({ required: true }) license: any;
  @Input({ required: true }) version: string = '';
  @Input({ required: true }) settings: any;
  @Input({ required: true }) remoteSourcesList: any;

  constructor(private _licenseService: LicenseService) {}

  public assistantBeingEdited: any | null = null;
  public selectedTab: string = 'general';

  public onOpenAssistantEdit(assistant: any) {
    this.assistantBeingEdited = assistant;
  }

  public closeAssistantEdit() {
    // When closing the assistant edit overlay, select the Assistants tab
    this.selectedTab = 'assistants';
    this.assistantBeingEdited = null;
  }

  public closeModal() {
    // Clear edit state so reopening the dialog starts in normal view
    this.assistantBeingEdited = null;
    this.isOpenChange.emit(false);
  }

  public async onBrandingSettingsUpdated(brandingSettings: BrandingSettings) {
    const result = await this._licenseService.updateSettings(
      brandingSettings,
      'branding',
      this.license.id,
    );
    if (result === 'ok') {
      // Clear the cache to force fresh data fetch
      this._licenseService.clearSettingsCache(this.license.id);
      // Refresh the settings to get the updated branding data
      await this._licenseService.fetchLicenseSettings(this.license.id);
      // Update the local settings reference
      this.settings = structuredClone(this._licenseService.licenseSettings$());
    }
  }

  public onStatusUpdated(newStatus: LicenseStatus) {
    if (!this.license?.id) return;
    this.license = {
      ...this.license,
      status: newStatus,
    };
    this.licenseUpdated.emit({
      id: this.license.id,
      status: newStatus,
    });
  }
}
